import React, { useEffect, useState } from "react";
import {
  Grid2,
  Box,
  Typography,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import FullScreenDialog from "../../Components/FullScreenDialog";
import CloseButton from "../../Components/CloseButton";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import CustomIcon from "../../Components/CustomIcon";
import { getDigitalTwinHistory, getDigitalTwinTagsHistory } from "../../Api/DigitalTwin";
import { getBatteryServiceRecords } from "../../Api/ServiceRecordApi";
import { getLocalDateFromUTC } from "../../Helper/DatePicker/DateConverter";
import {
  buildVehicleDetailsDescription,
  getUpdatedChanges,
} from "../../Helper/DigitalTwin/EventHistoryHelper";

const EventHistory = ({ batteryID }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [eventData, setEventData] = useState({
    vehicleDetails: [],
    operations: [],
    componentReplacements: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Helper function to format date and time
  const formatDateTime = (dateTimeString) => {
    try {
      // Get date in format "DD MMM YYYY"
      const formattedDate = getLocalDateFromUTC(dateTimeString, "DD MMMM YYYY");
      // Get time in format "h:mm A"
      const formattedTime = getLocalDateFromUTC(dateTimeString, "h:mm A");

      return {
        date: formattedDate,
        time: formattedTime
      };
    } catch (e) {
      return { date: "", time: "" };
    }
  };

  // Helper function to process vehicle details history
  const processVehicleDetailsHistory = (historyData) => {
    const eventsByDate = {};

    historyData.forEach(event => {
      const { date, time } = formatDateTime(event.createdAt);

      if (!eventsByDate[date]) {
        eventsByDate[date] = [];
      }

      const description = buildVehicleDetailsDescription(event);

      eventsByDate[date].push({
        time: time,
        description: description,
        changes: getUpdatedChanges(event),
        remark: event.userRemarks || null,
        actionType: event.actionType,
        createdBy: event.createdByName,
        metadata: {
          licencePlateNumber: event.licencePlateNumber,
          chassisNumber: event.chassisNumber,
          simNumber: event.simNumber,
          msisdn: event.msisdn,
          imei: event.imei,
          fleetName: event.fleetName,
          vehicleApplication: event.vehicleApplication,
          protocolName: event.protocolName,
          onboardingCity: event.onboardingCity,
          onboardingState: event.onboardingState,
          onboardingDepot: event.onboardingDepot
        }
      });
    });

    // Convert to array format
    return Object.entries(eventsByDate).map(([date, events]) => ({
      date: date,
      events: events
    }));
  };

  // Helper function to process operations/tags history
  const processOperationsHistory = (tagsHistoryData) => {
    const eventsByDate = {};

    tagsHistoryData.forEach(event => {
      const { date, time } = formatDateTime(event.createdAt);

      if (!eventsByDate[date]) {
        eventsByDate[date] = [];
      }

      // Create description based on action type
      let description = "";
      if (event.actionType === "assign") {
        description = `@${event.createdByName} assigned status "${event.valueDisplayName}"`;
      } else if (event.actionType === "update") {
        description = `@${event.createdByName} updated status to "${event.valueDisplayName}"`;
      } else {
        description = `@${event.createdByName} ${event.actionType} ${event.tagName}`;
      }

      eventsByDate[date].push({
        time: time,
        description: description,
        remark: event.newCustomNotes || null,
        actionType: event.actionType,
        createdBy: event.createdByName,
        metadata: {
          tagCode: event.tagCode,
          tagName: event.tagName,
          valueCode: event.valueCode,
          valueDisplayName: event.valueDisplayName
        }
      });
    });

    // Convert to array format
    return Object.entries(eventsByDate).map(([date, events]) => ({
      date: date,
      events: events
    }));
  };

  // Helper function to process component (pack) replacement history. Each pack
  // replacement in a service record becomes one event, grouped by recorded date.
  const processComponentReplacementHistory = (records) => {
    const eventsByDate = {};

    records.forEach(record => {
      const sd = record?.serviceDetails || {};
      const recordedAt = sd.recordedAt || record?.recordedAt;
      const { date, time } = formatDateTime(recordedAt);
      const recordedByName = sd.recordedByName || record?.recordedByName;
      const packs = record?.packReplacementDetails || [];

      packs.forEach(p => {
        const location = [p.pack, p.string].filter(Boolean).join(", ");
        const swap =
          p.outgoingSerial || p.incomingSerial
            ? ` (${p.outgoingSerial || "-"} → ${p.incomingSerial || "-"})`
            : "";
        const who = recordedByName ? `@${recordedByName} ` : "";
        const description = `${who}replaced ${location || "pack"}${swap}`;

        if (!eventsByDate[date]) eventsByDate[date] = [];
        eventsByDate[date].push({
          time,
          description,
          remark: sd.remarks || record?.remarks || null,
          actionType: "REPLACEMENT",
          createdBy: recordedByName,
          metadata: {
            pack: p.pack,
            string: p.string,
            outgoingSerial: p.outgoingSerial,
            incomingSerial: p.incomingSerial,
          },
        });
      });
    });

    return Object.entries(eventsByDate).map(([date, events]) => ({ date, events }));
  };

  const fetchEventHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all three histories in parallel.
      const [vehicleResponse, operationsResponse, serviceRecordsResponse] = await Promise.all([
        getDigitalTwinHistory(batteryID),
        getDigitalTwinTagsHistory(batteryID, 'status'),
        getBatteryServiceRecords(batteryID),
      ]);

      // Process vehicle details history
      const vehicleHistoryData = vehicleResponse?.response?.data || [];
      const vehicleDetails = vehicleHistoryData.length > 0
        ? processVehicleDetailsHistory(vehicleHistoryData)
        : [];

      // Process operations/tags history
      const operationsHistoryData = operationsResponse?.response?.data || [];
      const operations = operationsHistoryData.length > 0
        ? processOperationsHistory(operationsHistoryData)
        : [];

      // Process component (pack) replacement history.
      const serviceRecords =
        serviceRecordsResponse?.response?.data ||
        serviceRecordsResponse?.response?.data?.records ||
        [];
      const records = Array.isArray(serviceRecords)
        ? serviceRecords
        : serviceRecords?.records || [];
      const componentReplacements = records.length > 0
        ? processComponentReplacementHistory(records)
        : [];

      setEventData({ vehicleDetails, operations, componentReplacements });
    } catch (err) {
      console.error("Error fetching event history:", err);
      setError("Failed to load event history");
      setEventData({ vehicleDetails: [], operations: [], componentReplacements: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && batteryID) {
      fetchEventHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batteryID]);

  const renderEventItem = (event, index) => (
    <Box key={index} sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Typography variant="fw400s14lh24c686868" sx={{ color: '#8F8F8F', minWidth: '80px' }}>
          {event.time}
        </Typography>
        <Box>
          <Typography variant="fw400s14lh24c686868" sx={{color: '#494949'}}>
            {event.description}
          </Typography>
          {/* What changed: one row per field, "previous -> new", set apart from who
              changed it. */}
          {event.changes?.length > 0 && (
            <Box
              data-testid="event-history-changes"
              sx={{
                mt: 1,
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                columnGap: 2,
                rowGap: 0.5,
                bgcolor: '#F9F9F9',
                borderRadius: '8px',
                px: 1.5,
                py: 1,
              }}
            >
              {event.changes.map(({ label, previous, value }) => (
                <React.Fragment key={label}>
                  <Typography sx={{ fontSize: '13px', color: '#686868' }}>{label}</Typography>
                  <Typography component="div" sx={{ fontSize: '13px', overflowWrap: 'anywhere' }}>
                    {previous && (
                      <>
                        <Box component="span" sx={{ color: '#8F8F8F' }}>{previous}</Box>
                        <Box component="span" sx={{ color: '#8F8F8F', mx: 0.75 }}>→</Box>
                      </>
                    )}
                    <Box component="span" sx={{ fontWeight: 600, color: '#2E2E2E' }}>{value}</Box>
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}
          {event.remark && (
            <Box sx={{
              mt: 1,
              pl: 2,
              bgcolor: '#F9F9F9',
              p: 1.5,
              borderRadius: '8px',
              display: 'flex',
              gap: 1.5
            }}>
              <CustomIcon name="comment-icon" sx={{mt: 0.5}} />
              <Box>
                <Typography variant="fw400s14lh20c2E2E2E" display="block" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Remark:
                </Typography>
                <Typography variant="fw400s14lh20c2E2E2E">
                  {event.remark}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderDateGroup = (dateGroup, groupIndex, isLast) => (
    <Box key={groupIndex} sx={{ mb: 3 }}>
      <Typography variant="fw500s14c000000" display='block' sx={{ lineHeight: '24px', mb: 2 }}>
        {dateGroup.date}
      </Typography>
      {dateGroup.events.map((event, eventIndex) => renderEventItem(event, eventIndex))}
      {!isLast && (
        <Divider sx={{ borderColor: "#EAEAEA", mt: 0 }} />
      )}
    </Box>
  );

  return (
    <Box>
      <PrimaryTooltip title="Event History">
        <Box
          onClick={handleOpen}
          sx={{
            cursor: "pointer",
            display: 'flex',
            alignItems: 'center',
            '&:hover svg': {
              '& path': { stroke: "#4D44D8" }
            }
          }}
        >
          <CustomIcon name="history-icon" fill="#8F8F8F" />
        </Box>
      </PrimaryTooltip>
      <FullScreenDialog
        open={open}
        handleClose={handleClose}
        containerStyles={{
          overflowY: "hidden",
          overflowX: "hidden",
          ml: "auto",
          width: "calc(100%/3)",
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 3, flexShrink: 0 }}>
            <Grid2
              container
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CustomIcon name="history-icon" fill="#686868" width="20" height="20" />
                <Typography variant="fw600s16c000000" sx={{ fontSize: '20px', lineHeight: "20px", color: '#101111' }}>
                  Event History
                </Typography>
              </Box>
              <CloseButton onClick={handleClose} />
            </Grid2>

            <Box sx={{ mt: 2.5, mx: -3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: '40px',
                  '& .MuiTab-root': {
                    minHeight: '40px',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#8D8D8D',
                    '&.Mui-selected': {
                      color: '#494949',
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#494949',
                  }
                }}
              >
                <Tab label="Vehicle Details" />
                <Tab label="Operations" />
                <Tab label="Component Replacement" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3.5,
            pb: 3,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress size={40} />
              </Box>
            ) : error ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="fw400s14lh24c686868" color="error">
                  {error}
                </Typography>
              </Box>
            ) : (
              <>
                {activeTab === 0 && (
                  <Box>
                    {eventData.vehicleDetails.length > 0 ? (
                      eventData.vehicleDetails.map((dateGroup, index) =>
                        renderDateGroup(dateGroup, index, index === eventData.vehicleDetails.length - 1)
                      )
                    ) : (
                      <Typography variant="fw400s14lh24cA2A2A2" sx={{ textAlign: 'center', mt: 4 }}>
                        No vehicle details events available
                      </Typography>
                    )}
                  </Box>
                )}
                {activeTab === 1 && (
                  <Box>
                    {eventData.operations.length > 0 ? (
                      eventData.operations.map((dateGroup, index) =>
                        renderDateGroup(dateGroup, index, index === eventData.operations.length - 1)
                      )
                    ) : (
                      <Typography variant="fw400s14lh24cA2A2A2" sx={{ textAlign: 'center', mt: 4 }}>
                        No operations events available
                      </Typography>
                    )}
                  </Box>
                )}
                {activeTab === 2 && (
                  <Box>
                    {eventData.componentReplacements.length > 0 ? (
                      eventData.componentReplacements.map((dateGroup, index) =>
                        renderDateGroup(dateGroup, index, index === eventData.componentReplacements.length - 1)
                      )
                    ) : (
                      <Typography variant="fw400s14lh24cA2A2A2" sx={{ textAlign: 'center', mt: 4 }}>
                        No component replacement events available
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </FullScreenDialog>
    </Box>
  );
};

export default EventHistory;
